import React, { useState, useEffect, useCallback } from 'react';
import { FaSpinner, FaExclamationTriangle, FaFilter, FaSort } from 'react-icons/fa';
import { useNotification } from '../../context/NotificationContext';
import NotificationItem from './NotificationItem';
import { NOTIFICATION_CATEGORIES, CATEGORY_METADATA } from '../../api/notifications';
import styles from './NotificationList.module.css';

/**
 * NotificationList Component
 * 
 * Full-featured notification list for dedicated notification page with:
 * - Advanced filtering and sorting
 * - Infinite scroll pagination
 * - Bulk actions
 * - Search functionality
 * - Category grouping
 * - Export functionality
 */
const NotificationList = ({ 
  category = null,
  showFilters = true,
  showSearch = true,
  showBulkActions = true,
  pageSize = 20,
  className = ''
}) => {
  // State
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  
  // Filters and sorting
  const [activeCategory, setActiveCategory] = useState(category || 'all');
  const [activeType, setActiveType] = useState('all');
  const [activePriority, setActivePriority] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all'); // all, read, unread
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  
  // UI state
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  
  const {
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    unreadCountByCategory
  } = useNotification();

  // Sync activeCategory with category prop when it changes
  useEffect(() => {
    const newCategory = category || 'all';
    setActiveCategory(newCategory);
    // Reset type filter when category changes from parent
    setActiveType('all');
  }, [category]);

  /**
   * Build query parameters for API call
   */
  const buildQueryParams = useCallback((offset = 0) => {
    const params = {
      limit: pageSize,
      offset,
      ...(activeCategory !== 'all' && { category: activeCategory }),
      ...(activeType !== 'all' && { type: activeType }),
      ...(activePriority !== 'all' && { priority: activePriority }),
      ...(activeStatus === 'unread' && { unread: 'true' }),
      ...(activeStatus === 'read' && { read: 'true' }),
      ...(searchQuery && { search: searchQuery }),
      sortBy,
      sortOrder
    };
    
    return params;
  }, [activeCategory, activeType, activePriority, activeStatus, searchQuery, sortBy, sortOrder, pageSize]);

  /**
   * Load notifications
   */
  const loadNotifications = useCallback(async (offset = 0, append = false) => {
    try {
      if (offset === 0) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      
      const params = buildQueryParams(offset);
      const response = await fetchNotifications(params);
      
      let newNotifications = response.notifications || [];
      
      // Client-side filtering as fallback if API doesn't filter
      // This ensures categories work even if backend doesn't support filtering
      if (activeCategory !== 'all' && newNotifications.length > 0) {
        newNotifications = newNotifications.filter(n => n.category === activeCategory);
      }
      
      if (append) {
        setNotifications(prev => [...prev, ...newNotifications]);
      } else {
        setNotifications(newNotifications);
        setSelectedNotifications(new Set()); // Clear selection on new load
      }
      
      setHasMore(response.pagination?.hasMore || false);
      
    } catch (err) {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildQueryParams, fetchNotifications, activeCategory]);

  // Load notifications when filters change
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  /**
   * Handle infinite scroll
   */
  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop
      >= document.documentElement.offsetHeight - 1000
      && hasMore
      && !loadingMore
    ) {
      loadNotifications(notifications.length, true);
    }
  }, [hasMore, loadingMore, notifications.length, loadNotifications]);

  // Set up scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  /**
   * Handle filter changes
   */
  const handleCategoryChange = (newCategory) => {
    setActiveCategory(newCategory);
    setActiveType('all'); // Reset type filter when category changes
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  /**
   * Bulk actions
   */
  const handleSelectAll = () => {
    const validNotifications = notifications.filter(n => n && n._id);
    if (selectedNotifications.size === validNotifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(validNotifications.map(n => n._id)));
    }
  };

  const handleSelectNotification = (notificationId) => {
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(notificationId)) {
      newSelected.delete(notificationId);
    } else {
      newSelected.add(notificationId);
    }
    setSelectedNotifications(newSelected);
  };

  const handleBulkMarkAsRead = async () => {
    const unreadSelected = notifications.filter(n => 
      selectedNotifications.has(n._id) && !n.read
    );
    
    await Promise.all(
      unreadSelected.map(n => markAsRead(n._id))
    );
    
    await loadNotifications();
    setSelectedNotifications(new Set());
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleBulkDelete = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setShowDeleteConfirm(false);
    await Promise.all(
      Array.from(selectedNotifications).map(id => deleteNotification(id))
    );
    await loadNotifications();
    setSelectedNotifications(new Set());
  };

  /**
   * Get available notification types for current category
   */
  const getAvailableTypes = () => {
    if (activeCategory === 'all') {
      return Object.values(CATEGORY_METADATA).flatMap(cat => cat.types);
    }
    
    return CATEGORY_METADATA[activeCategory]?.types || [];
  };

  // Filter options
  const categoryOptions = [
    { key: 'all', name: 'All Categories', count: notifications.length },
    ...Object.entries(CATEGORY_METADATA).map(([key, meta]) => ({
      key,
      name: meta.name,
      icon: meta.icon,
      count: unreadCountByCategory[key] || 0
    }))
  ];

  const typeOptions = [
    { key: 'all', name: 'All Types' },
    ...getAvailableTypes().map(type => ({
      key: type,
      name: type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }))
  ];

  const priorityOptions = [
    { key: 'all', name: 'All Priorities' },
    { key: 'urgent', name: 'Urgent' },
    { key: 'high', name: 'High' },
    { key: 'medium', name: 'Medium' },
    { key: 'low', name: 'Low' }
  ];

  const statusOptions = [
    { key: 'all', name: 'All' },
    { key: 'unread', name: 'Unread' },
    { key: 'read', name: 'Read' }
  ];

  const sortOptions = [
    { key: 'createdAt', name: 'Date' },
    { key: 'priority', name: 'Priority' },
    { key: 'type', name: 'Type' },
    { key: 'read', name: 'Status' }
  ];

  return (
    <div className={`${styles.notificationList} ${className}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h2 className={styles.title}>
            Notifications
            {notifications.length > 0 && (
              <span className={styles.count}>({notifications.length})</span>
            )}
          </h2>
        </div>
        
        <div className={styles.headerActions}>
          {/* Search */}
          {showSearch && (
            <div className={styles.searchBox}>
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          )}
          
          {/* Filter toggle */}
          {showFilters && (
            <button
              className={`${styles.filterToggle} ${filtersExpanded ? styles.active : ''}`}
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              title="Toggle filters"
            >
              <FaFilter />
            </button>
          )}
          
          {/* Bulk actions toggle */}
          {showBulkActions && notifications.length > 0 && (
            <button
              className={`${styles.bulkToggle} ${isSelectMode ? styles.active : ''}`}
              onClick={() => {
                setIsSelectMode(!isSelectMode);
                setSelectedNotifications(new Set());
              }}
              title="Toggle selection mode"
            >
              Select
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && filtersExpanded && (
        <div className={styles.filters}>
          <div className={styles.filterRow}>
            {/* Category filter */}
            <select
              value={activeCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className={styles.filterSelect}
            >
              {categoryOptions.map(option => (
                <option key={option.key} value={option.key}>
                  {option.name} {option.count > 0 && `(${option.count})`}
                </option>
              ))}
            </select>
            
            {/* Type filter */}
            <select
              value={activeType}
              onChange={(e) => setActiveType(e.target.value)}
              className={styles.filterSelect}
            >
              {typeOptions.map(option => (
                <option key={option.key} value={option.key}>
                  {option.name}
                </option>
              ))}
            </select>
            
            {/* Priority filter */}
            <select
              value={activePriority}
              onChange={(e) => setActivePriority(e.target.value)}
              className={styles.filterSelect}
            >
              {priorityOptions.map(option => (
                <option key={option.key} value={option.key}>
                  {option.name}
                </option>
              ))}
            </select>
            
            {/* Status filter */}
            <select
              value={activeStatus}
              onChange={(e) => setActiveStatus(e.target.value)}
              className={styles.filterSelect}
            >
              {statusOptions.map(option => (
                <option key={option.key} value={option.key}>
                  {option.name}
                </option>
              ))}
            </select>
            
            {/* Sort */}
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className={styles.filterSelect}
            >
              {sortOptions.map(option => (
                <React.Fragment key={option.key}>
                  <option value={`${option.key}-desc`}>
                    {option.name} (Newest First)
                  </option>
                  <option value={`${option.key}-asc`}>
                    {option.name} (Oldest First)
                  </option>
                </React.Fragment>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Bulk actions */}
      {isSelectMode && (
        <div className={styles.bulkActions}>
          <div className={styles.bulkSelection}>
            <label className={styles.selectAllLabel}>
              <input
                type="checkbox"
                checked={selectedNotifications.size === notifications.length && notifications.length > 0}
                onChange={handleSelectAll}
              />
              Select All ({selectedNotifications.size} selected)
            </label>
          </div>
          
          {selectedNotifications.size > 0 && (
            <div className={styles.bulkButtons}>
              <button
                className={styles.bulkButton}
                onClick={handleBulkMarkAsRead}
              >
                Mark as Read
              </button>
              <button
                className={`${styles.bulkButton} ${styles.deleteButton}`}
                onClick={handleBulkDelete}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmDialog}>
            <p>Delete {selectedNotifications.size} notification{selectedNotifications.size !== 1 ? 's' : ''}?</p>
            <div className={styles.confirmActions}>
              <button className={styles.confirmCancel} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className={`${styles.bulkButton} ${styles.deleteButton}`} onClick={confirmBulkDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={styles.content}>
        {loading ? (
          <div className={styles.loading}>
            <FaSpinner className={styles.spinner} />
            <span>Loading notifications...</span>
          </div>
        ) : error ? (
          <div className={styles.error}>
            <FaExclamationTriangle className={styles.errorIcon} />
            <span>{error}</span>
            <button 
              className={styles.retryButton}
              onClick={() => loadNotifications()}
            >
              Try Again
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>📭</div>
            <div className={styles.emptyTitle}>No notifications found</div>
            <div className={styles.emptyMessage}>
              {searchQuery 
                ? `No notifications match "${searchQuery}"`
                : 'New notifications will appear here when they arrive.'
              }
            </div>
          </div>
        ) : (
          <>
            {/* Notification items */}
            <div className={styles.notifications}>
              {notifications.filter(notification => notification && notification._id).map(notification => (
                <div key={notification._id} className={styles.notificationWrapper}>
                  {isSelectMode && (
                    <div className={styles.selectCheckbox}>
                      <input
                        type="checkbox"
                        checked={selectedNotifications.has(notification._id)}
                        onChange={() => handleSelectNotification(notification._id)}
                      />
                    </div>
                  )}
                  <NotificationItem
                    notification={notification}
                    onRead={() => loadNotifications()}
                    showCategory={activeCategory === 'all'}
                    showActions={!isSelectMode}
                  />
                </div>
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div className={styles.loadMore}>
                {loadingMore ? (
                  <div className={styles.loadingMore}>
                    <FaSpinner className={styles.spinner} />
                    <span>Loading more...</span>
                  </div>
                ) : (
                  <button
                    className={styles.loadMoreButton}
                    onClick={() => loadNotifications(notifications.length, true)}
                  >
                    Load More Notifications
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationList;
